import React, { useRef, useEffect, useState } from 'react';
import { promoteAndRound } from '../../utils/unitDisplay';
import ShoppingListEmptyState from '../EmptyStates/ShoppingListEmptyState';
import {
  AiOutlineUndo,
  AiOutlineEdit,
  AiOutlinePlus,
  AiOutlineCheck,
  AiOutlineDelete,
} from 'react-icons/ai';

export type Item = {
  ingredientId?: string;
  name: string;
  unit: string;
  /** now can be a number, a pure‐qualitative string like "A", or null for “to taste” */
  requiredAmount: number | string | null;
  haveAmount: number;
  isRemoved: boolean;
  mode: 'active' | 'input' | 'noNeed';
  isCustom?: boolean;
};

type IngredientListProps = {
  items: Item[];
  setItems: React.Dispatch<React.SetStateAction<Item[]>>;
  /** signature now allows qualitative */
  formatQuantity: (
    requiredAmount: number | string | null,
    haveAmount: number,
    unit: string
  ) => string;
};

const IngredientList: React.FC<IngredientListProps> = ({
  items,
  setItems,
  formatQuantity,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [extraName, setExtraName] = useState('');
  const [extraQty, setExtraQty] = useState('');

  const collapseAll = () => {
    setItems((prev) =>
      prev.map((it) => (it.mode === 'input' ? { ...it, mode: 'active' } : it))
    );
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        collapseAll();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const enterInput = (id: string) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.mode === 'input') {
          return { ...it, mode: 'active' };
        }
        if (it.ingredientId === id) {
          return { ...it, mode: 'input' };
        }
        return it;
      })
    );
  };

  const updateHave = (id: string, val: string) =>
    setItems((prev) =>
      prev.map((it) =>
        it.ingredientId === id
          ? { ...it, haveAmount: parseFloat(val) || 0 }
          : it
      )
    );

  const confirmHave = (id: string) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.ingredientId !== id) return it;
        // If they typed something non-numeric, leave haveAmount as-is:
        const sanitized = isNaN(it.haveAmount) ? it.haveAmount : it.haveAmount;
        return {
          ...it,
          haveAmount: sanitized,
          mode: 'active',
        };
      })
    );
  };

  const markNoNeed = (id: string) => {
    setItems((prev) =>
      prev.map((it) =>
        it.ingredientId === id
          ? { ...it, mode: 'noNeed', haveAmount: 0, isRemoved: true }
          : it
      )
    );
  };

  const undoNoNeed = (id: string) =>
    setItems((prev) =>
      prev.map((it) =>
        it.ingredientId === id ? { ...it, mode: 'active' } : it
      )
    );

  const addExtra = () => {
    if (!extraName.trim()) return;
    const id = (crypto as any).randomUUID?.() || `custom-${Date.now()}`;
    const parsedQty = parseFloat(extraQty);
    const newItem: Item = {
      ingredientId: id,
      name: extraName.trim(),
      unit: '',
      requiredAmount: isNaN(parsedQty) ? 0 : parsedQty,
      haveAmount: 0,
      isRemoved: false,
      mode: 'active',
      isCustom: true,
    };
    setItems((prev) => [newItem, ...prev]);
    setExtraName('');
    setExtraQty('');
  };

  return (
    <div ref={containerRef} className="space-y-2">
      {items.length === 0
        ? <ShoppingListEmptyState />
        : (
        <>
          {/* Add Extra */}
          <div className="grid grid-cols-[1fr_auto_auto] items-center p-3 mb-2 gap-x-2 border rounded-lg
             focus-within:ring-1 focus-within:ring-primary focus-within:ring-opacity-50 focus-within:shadow-md transition">
            <input
              type="text"
              placeholder="Anything else?"
              value={extraName}
              onChange={(e) => setExtraName(e.target.value)}
              className="w-full text-sm border-none focus:ring-0 focus:outline-none"
            />
            <div className="px-4">
              <input
                type="number"
                placeholder="Amount"
                value={extraQty}
                onChange={(e) => setExtraQty(e.target.value)}
                className="w-16 text-sm text-right border-none bg-transparent focus:outline-none border-b border-gray-400 leading-snug"
              />
            </div>
            <button
              onClick={addExtra}
              disabled={!extraName.trim()}
              className={`p-1 ${
                extraName.trim()
                  ? 'text-green-600 hover:text-green-800'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              <AiOutlinePlus size={20} />
            </button>
          </div>

          {/* Ingredient items */}
          {items.map((it) => {
            const {
              ingredientId,
              name,
              unit,
              requiredAmount,
              haveAmount,
              mode,
              isRemoved,
            } = it;
            // handle numeric vs. qualitative amounts
            let roundedRequiredValue: number | string | null;
            if (typeof requiredAmount === 'number') {
              const { amount } = promoteAndRound(requiredAmount, unit);
              roundedRequiredValue = amount;
            } else {
              // for "A" or null
              roundedRequiredValue = requiredAmount;
            }
            const remaining =
              typeof roundedRequiredValue === 'number'
                ? Math.max(0, roundedRequiredValue - haveAmount)
                : 0;
            const isInput = mode === 'input';
            const isNoNeed = mode === 'noNeed';
            const formattedQty = formatQuantity(
              requiredAmount,
              haveAmount,
              unit
            );

            return (
              <div
                key={ingredientId}
                className={`transition-all duration-300 overflow-hidden rounded-lg border ${
                  isInput
                    ? 'bg-gray-100 ring-1 ring-primary ring-opacity-50'
                    : isNoNeed
                    ? 'bg-gray-100 opacity-60'
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                {!isInput ? (
                  <div className="grid grid-cols-[1fr_auto_auto] items-center p-3 gap-x-2">
                    <div className="truncate">
                      <span
                        className={`font-medium ${
                          isNoNeed
                            ? 'line-through text-gray-500'
                            : 'text-gray-800'
                        }`}
                      >
                        {name}
                      </span>
                    </div>
                    <div className="text-right text-sm text-gray-600 px-4">
                      {formattedQty}
                    </div>
                    <div className="flex items-center">
                      {isNoNeed ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            undoNoNeed(ingredientId!);
                          }}
                          className="p-1 text-gray-600"
                        >
                          <AiOutlineUndo size={18} />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            enterInput(ingredientId!);
                          }}
                          className="p-1 text-primary hover:bg-gray-200 rounded"
                        >
                          <AiOutlineEdit size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-[auto_1fr_auto_auto_auto] items-center p-3 gap-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markNoNeed(ingredientId!);
                      }}
                      className="p-1 text-red-500 hover:text-red-700"
                    >
                      <AiOutlineDelete size={18} />
                    </button>
                    <div className="truncate font-medium text-gray-800">
                      {name}
                    </div>
                    <input
                      type="number"
                      min={0}
                      value={String(
                        promoteAndRound(requiredAmount as number, unit).amount
                      )}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setItems((prev) =>
                          prev.map((item) =>
                            item.ingredientId === ingredientId
                              ? {
                                  ...item,
                                  requiredAmount: isNaN(val) ? 0 : val,
                                }
                              : item
                          )
                        );
                      }}
                      placeholder={`${promoteAndRound(
                        requiredAmount as number,
                        unit
                      ).amount}`}
                      autoFocus
                      className="w-20 text-sm text-right rounded border border-gray-300 px-1 py-0.5 focus:border-primary focus:outline-none transition-shadow shadow-sm focus:shadow-outline"
                    />
                    <span className="text-sm text-gray-700">{unit}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmHave(ingredientId!);
                      }}
                      className="p-1 text-green-600 hover:text-green-800"
                    >
                      <AiOutlineCheck size={18} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default IngredientList;
